import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { TapCheckbox } from '@/components/core/common/TapCheckbox';
import { useFilterLayout } from '@/components/features/mindspace/filters/filterLayout';

/**
 * Source-lesson picker - checkbox list (not pills) because lessons are
 * entities with titles of varying length, not free-form tags. Long
 * titles would wrap awkwardly as pills; rows handle them cleanly.
 * Typeahead appears once we have more than 6 lessons so the picker
 * scales when the user's library grows.
 */
export function SourceLessonList({
  options,
  selected,
  onToggle,
}: {
  options: Array<{ id: string; title: string }>;
  selected: string[];
  onToggle: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  // The phone sheet: 44px rows with the box at the trailing edge, and no
  // scroller of their own inside the sheet's.
  const sheet = useFilterLayout() === 'sheet';
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const showSearch = options.length > 6;

  // Render in the incoming order (most-recent lesson first) and DON'T
  // reorder on selection - hoisting checked rows to the top makes the row
  // jump out from under the cursor on click. Selection stays visible via
  // the checkbox + row highlight instead.
  const visible = useMemo(() => {
    if (!search.trim()) return options;
    const needle = search.trim().toLowerCase();
    return options.filter(o => o.title.toLowerCase().includes(needle));
  }, [options, search]);

  return (
    <div className='space-y-2'>
      {showSearch && (
        <div className='relative'>
          <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
          <input
            type='text'
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder='Search lessons…'
            className='w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-2 text-sm text-foreground !outline-none placeholder:text-muted-foreground/60 focus:border-brand-400 focus:!outline-none focus:!ring-0 focus:!ring-offset-0 focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0 dark:focus:border-primary max-sm:h-11 max-sm:text-base'
          />
        </div>
      )}
      {visible.length === 0 ? (
        <p className='py-1 text-xs text-muted-foreground'>
          No lessons match your search.
        </p>
      ) : (
        <ul
          className={
            sheet
              ? 'divide-y divide-border overflow-hidden rounded-xl border border-border bg-card'
              : 'max-h-56 divide-y divide-border overflow-y-auto rounded-lg border border-border'
          }
        >
          {visible.map(opt => {
            const checked = selectedSet.has(opt.id);
            return (
              <li key={opt.id}>
                <label
                  className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm transition-colors ${sheet ? 'min-h-12 justify-between px-4' : ''} ${
                    checked
                      ? 'dark:tint-brand bg-brand-50/60 text-brand-900'
                      : 'text-foreground hover:bg-muted dark:hover:bg-gray-800/40'
                  }`}
                >
                  {sheet ? (
                    <TapCheckbox
                      checked={checked}
                      onChange={() => onToggle(opt.id)}
                      className='order-last'
                    />
                  ) : (
                    <input
                      type='checkbox'
                      checked={checked}
                      onChange={() => onToggle(opt.id)}
                      className='h-3.5 w-3.5 cursor-pointer rounded-sm border-gray-300 accent-primary focus:ring-ring dark:border-gray-600'
                    />
                  )}
                  <span className='min-w-0 flex-1 truncate'>{opt.title}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
